import { AnyAction, createSlice, PayloadAction, ThunkAction } from "@reduxjs/toolkit";
import { saveAs } from "file-saver";
import { logError } from "../../../utils/utils";
import { AppDispatch, RootState } from "../../../app/store";
import { errorHandler } from "../../../utils/errorHandling";
import { aktiverMesspunkt, betriebsereignis, messgeraet, messstelle } from "../../../utils/apiTypes";
import { getWithAuth, putWithAuth, getXLSXWithAuth } from "../../../utils/api";
import { openSnackbar } from "../../../features/snackbar/globalSnackbarSlice";
import { ipVerwaltung, firewallRequestDeviceDto } from "./ipVerwaltungTypes";
import { GridFilterModel, GridRowModesModel } from "@mui/x-data-grid-premium";

export const emptyAktiverMesspunkt: aktiverMesspunkt = {
  name: "",
  schaltfeld: "",
  umspannwerk: "",
  messgeraet: 0,
  router: 0,
};

export const emptyGeraet: messgeraet = {
  id: 0,
  seriennummer: "",
  herstellungsjahr: 0,
  firmware: "",
  macadresse: "",
  kommentar: "",
  inbetriebnahmedatum: "",
  wartungsintervall: "",
  zeitsynchronisationsart: "",
  typ: 0,
  ipadresse: "",
  subnetzmaske: "",
  zugeordnet: false,
  jemalsZugeordnet: false,
};

const initialState: ipVerwaltung = {
  aktiveMesspunkte: [],
  selectedUW: "",
  isDocumentAlreadyAvailable: false,
  newMode: false,
  messpunktLoading: false,
  selectedAktiverMesspunkt: {} as messstelle,
  geraetDaten: emptyGeraet,
  betriebsereignisse: [],
  isDokumentLoading: false,
  hochlandenDialog: false,
  firewallRequestList: [],
  editMode: false,
  filterModel: { items: [] },
  rowModesModel: {},
};

const ipVerwaltungSlice = createSlice({
  name: "ipVerwaltung",
  initialState,
  reducers: {
    setIsDocumentAlreadyAvailable: (state, action: PayloadAction<boolean>) => {
      state.isDocumentAlreadyAvailable = action.payload;
    },
    setSelectedUW: (state, action: PayloadAction<string>) => {
      state.selectedUW = action.payload;
    },
    setAktiveMesspunkte: (state, action: PayloadAction<messstelle[]>) => {
      state.aktiveMesspunkte = action.payload;
    },
    setNewMode: (state, action: PayloadAction<boolean>) => {
      state.newMode = action.payload;
    },
    setMesspunktLoading: (state, action: PayloadAction<boolean>) => {
      state.messpunktLoading = action.payload;
    },
    setSelectedAktiverMesspunkt: (state, action: PayloadAction<messstelle>) => {
      state.selectedAktiverMesspunkt = action.payload;
    },
    setGeraetDaten: (state, action: PayloadAction<messgeraet>) => {
      state.geraetDaten = action.payload;
    },
    setBetriebsereignisse: (state, action: PayloadAction<betriebsereignis[]>) => {
      state.betriebsereignisse = action.payload;
    },
    setIsDokumentLoading: (state, action: PayloadAction<boolean>) => {
      state.isDokumentLoading = action.payload;
    },
    setHochlandenDialog: (state, action: PayloadAction<boolean>) => {
      state.hochlandenDialog = action.payload;
    },
    setFirewallRequestList: (state, action: PayloadAction<firewallRequestDeviceDto[]>) => {
      state.firewallRequestList = action.payload;
    },
    setEditMode: (state, action: PayloadAction<boolean>) => {
      state.editMode = action.payload;
    },
    setFilterModel: (state, action: PayloadAction<GridFilterModel>) => {
      state.filterModel = action.payload;
    },
    setRowModesModel: (state, action: PayloadAction<GridRowModesModel>) => {
      state.rowModesModel = action.payload;
    },
  },
});

export const {
  setIsDocumentAlreadyAvailable,
  setAktiveMesspunkte,
  setMesspunktLoading,
  setNewMode,
  setSelectedUW,
  setSelectedAktiverMesspunkt,
  setGeraetDaten,
  setBetriebsereignisse,
  setIsDokumentLoading,
  setHochlandenDialog,
  setFirewallRequestList,
  setEditMode,
  setFilterModel,
  setRowModesModel,
} = ipVerwaltungSlice.actions;

export const selectIsDocumentAlreadyAvailable = (state: RootState): boolean => state.ipVerwaltung.isDocumentAlreadyAvailable;
export const selectSelectedUW = (state: RootState): string => state.ipVerwaltung.selectedUW;
export const selectAktiveMesspunkte = (state: RootState): messstelle[] => state.ipVerwaltung.aktiveMesspunkte;
export const selectNewMode = (state: RootState): boolean => state.ipVerwaltung.newMode;
export const selectMesspunktLoading = (state: RootState): boolean => state.ipVerwaltung.messpunktLoading;
export const selectIsDokumentLoading = (state: RootState): boolean => state.ipVerwaltung.isDokumentLoading;
export const selectHochlandenDialog = (state: RootState): boolean => state.ipVerwaltung.hochlandenDialog;
export const selectFirewallRequestList = (state: RootState): firewallRequestDeviceDto[] => state.ipVerwaltung.firewallRequestList;
export const selectEditMode = (state: RootState): boolean => state.ipVerwaltung.editMode;
export const selectFilterModel = (state: RootState): GridFilterModel => state.ipVerwaltung.filterModel;
export const selectRowModesModel = (state: RootState): GridRowModesModel => state.ipVerwaltung.rowModesModel;

export const generateFirewallAntrag =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDokumentLoading(true));
    getXLSXWithAuth("GENERATEFIREWALLANTRAG", {}, uw)
      .then((response) => {
        const returnedFile = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(returnedFile, "Firewallantrag_" + uw.replace(" ", ""));
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const getAktiveMesspunkte =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(setMesspunktLoading(true));
    getWithAuth("MESSSTELLE", {})
      .then((response: messstelle[]) => {
        dispatch(setAktiveMesspunkte(response));
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      })
      .finally(() => dispatch(setMesspunktLoading(false)));
  };

export const getFirewallAntrag =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    const openInSameTab = (url: string) => {
      window.open(url, "_self", "noopener,noreferrer");
    };
    dispatch(setIsDokumentLoading(true));
    getWithAuth("GETFIREWALLANTRAG", {}, uw)
      .then((response) => {
        openInSameTab(response);
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const checkIfFirewallAntragFileIsAvailable =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDokumentLoading(true));
    getWithAuth("GETFIREWALLANTRAG", {}, uw)
      .then((response) => {
        if (response !== null) {
          dispatch(setIsDocumentAlreadyAvailable(true));
        } else {
          console.log("No documents available");
        }
      })
      .catch((error: any) => {
        dispatch(setIsDocumentAlreadyAvailable(false));
        logError(error);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const firewallRequestGenerateNI =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDokumentLoading(true));
    getXLSXWithAuth("GENERATENI", {}, uw)
      .then((response) => {
        const returnedFile = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(returnedFile, "antrag_ni_" + uw);
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const firewallRequestGenerateSI =
  (uw: string) =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDokumentLoading(true));
    getXLSXWithAuth("GENERATESI", {}, uw)
      .then((response) => {
        const returnedFile = new Blob([response], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(returnedFile, "antrag_si_" + uw);
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
      });
    dispatch(setIsDokumentLoading(false));
  };

export const sendFirewallRequest =
  (uw: string, firewallRequest: firewallRequestDeviceDto): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch: AppDispatch) => {
    dispatch(setIsDokumentLoading(true));
    putWithAuth("SENDFIREWALLANTRAG", {}, { firewallRequest }, uw)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        dispatch(
          openSnackbar({
            message: `Antrag für UW ${uw} wurde erfolgreich an SI gesendet.`,
            severity: "success",
            duration: 6000,
          })
        );
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
        dispatch(setIsDokumentLoading(false));
      });
  };

export const saveFirewallRequest =
  (id: number, firewallRequest: firewallRequestDeviceDto): ThunkAction<void, RootState, unknown, AnyAction> =>
  (dispatch: AppDispatch) => {
    dispatch(setIsDokumentLoading(true));
    putWithAuth("UPLOADFIREWALLANTRAG", {}, firewallRequest, id.toString())
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .then((response: any) => {
        dispatch(getFirewallRequest());
        dispatch(setIsDokumentLoading(false));
      })
      .catch((error: any) => {
        logError(error);
        errorHandler(error, dispatch);
        dispatch(setIsDokumentLoading(false));
      });
  };

export const getFirewallRequest =
  () =>
  (dispatch: AppDispatch): void => {
    dispatch(setIsDokumentLoading(true));
    getWithAuth("FIREWALLANTRAGREQUEST", {})
      .then((response: firewallRequestDeviceDto[]) => {
        dispatch(setFirewallRequestList(response));
      })
      .catch((error) => {
        logError(error);
        errorHandler(error, dispatch);
      })
      .finally(() => dispatch(setIsDokumentLoading(false)));
  };

export default ipVerwaltungSlice.reducer;
